/**
 * House-ad provider (ads epic — sc-6959). The bundled cross-promo creatives,
 * behind the provider-agnostic AdProvider interface.
 *
 * This is the ORIGINAL house-ad behavior (formerly inline in ad-slot.ts):
 * IAB 728x90 leaderboards under /ads/, no external requests, weighted
 * rotation that avoids repeating the current creative. It only serves
 * BANNERS — house ads have no video inventory, so interstitial/rewarded
 * report `unavailable` and the manager falls through to a network provider.
 *
 * Impression/click events go to OUR db (recordAdEvent), now tagged with
 * provider + format so house vs network CTR can be split.
 */

import { recordAdEvent } from '../api/client.js';
import { playHouseVideo } from './house-video-player.js';
import type {
  AdContext,
  AdFormat,
  AdProvider,
  BannerHandle,
  InterstitialResult,
  RewardedResult,
} from './provider.js';

export interface HouseAd {
  id: string;
  img: string;
  href: string;
  alt: string;
  weight: number;
}

/**
 * House VIDEO creative — the interstitial/rewarded counterpart to the 728x90
 * banners. Bundled landscape 960x540 mp4s under /ads/video/ (no external
 * requests — respects the cabinet-lock CSP), each with its own audio track
 * and click-through. Used by the interstitial/rewarded house fallback and,
 * pre-launch, as the ONLY video inventory until a network mediator goes live.
 */
export interface HouseVideoAd {
  id: string;
  /**
   * Advertiser/brand key. Rotation never plays the same brand twice in a
   * row, so a brand with several creatives (e.g. Privateer's 3 clips) still
   * cedes the next slot to a different advertiser. Clips within a brand rotate
   * among themselves across that brand's turns.
   */
  brand: string;
  /** Bundled 960x540 mp4 (with audio). */
  src: string;
  href: string;
  alt: string;
  /** Clip length seconds — informs the rewarded reward-gate / skip timer. */
  durationS: number;
  weight: number;
}

/** House cross-promo video creatives (see /ads/video/). All bundled
 *  960x540 mp4, transcoded from each product's master.
 *
 *  WEIGHTS give every BRAND equal airtime (~1/3 each) despite unequal clip
 *  counts: each brand's per-clip weights sum to 12, so parkpursuit's 4 clips
 *  are 3 apiece while privateer's and zyphr's 3 clips are 4 apiece. (LCM(4,3)
 *  keeps them integers.) Add a clip to a brand → rescale that brand's weights
 *  so they still total 12. */
export const HOUSE_VIDEO_ADS: readonly HouseVideoAd[] = [
  { id: 'parkpursuit-ai-guides', brand: 'parkpursuit', src: '/ads/video/ad_ai_park_guides.mp4', href: 'https://parkpursuit.app', alt: 'ParkPursuit — meet your AI park guides', durationS: 25, weight: 3 },
  { id: 'parkpursuit-influencer', brand: 'parkpursuit', src: '/ads/video/ad_social_influencer.mp4', href: 'https://parkpursuit.app', alt: 'ParkPursuit — it tracked my whole park day', durationS: 23, weight: 3 },
  { id: 'parkpursuit-whats-better', brand: 'parkpursuit', src: '/ads/video/ad_social_whatsbetter.mp4', href: 'https://parkpursuit.app', alt: 'ParkPursuit — record your park day', durationS: 14, weight: 3 },
  { id: 'parkpursuit-adventure', brand: 'parkpursuit', src: '/ads/video/ad_social_adventure.mp4', href: 'https://parkpursuit.app', alt: 'ParkPursuit — adventure together', durationS: 5, weight: 3 },
  { id: 'zyphr-oneapi', brand: 'zyphr', src: '/ads/video/ad_zyphr_oneapi.mp4', href: 'https://zyphr.dev', alt: 'Zyphr — one API for messaging, auth, and billing', durationS: 15, weight: 4 },
  { id: 'zyphr-vendors', brand: 'zyphr', src: '/ads/video/ad_zyphr_vendors.mp4', href: 'https://zyphr.dev', alt: 'Zyphr — six vendors, one tangle; replace them all', durationS: 15, weight: 4 },
  { id: 'zyphr-auth', brand: 'zyphr', src: '/ads/video/ad_zyphr_auth.mp4', href: 'https://zyphr.dev', alt: 'Zyphr — auth is a notification problem', durationS: 15, weight: 4 },
  { id: 'privateer-command-fleet', brand: 'privateer', src: '/ads/video/ad_privateer_command_fleet.mp4', href: 'https://bigego.games/games/privateerprofits', alt: 'Privateer Profits — command your fleet', durationS: 13, weight: 4 },
  { id: 'privateer-build-empire', brand: 'privateer', src: '/ads/video/ad_privateer_build_empire.mp4', href: 'https://bigego.games/games/privateerprofits', alt: 'Privateer Profits — build your empire', durationS: 13, weight: 4 },
  { id: 'privateer-set-sail', brand: 'privateer', src: '/ads/video/ad_privateer_set_sail.mp4', href: 'https://bigego.games/games/privateerprofits', alt: 'Privateer Profits — set sail', durationS: 13, weight: 4 },
];

/** Cross-promo across the Arcraz portfolio. Bundled 728x90 creatives. */
export const HOUSE_ADS: readonly HouseAd[] = [
  { id: 'parkpursuit', img: '/ads/parkpursuit-728x90.png', href: 'https://parkpursuit.app', alt: 'ParkPursuit — Walk. Ride. Win.', weight: 1 },
  { id: 'zyphr', img: '/ads/zyphr-728x90.png', href: 'https://zyphr.dev', alt: 'zyphr.dev — transactional messaging API', weight: 1 },
  { id: 'privateerprofits', img: '/ads/privateerprofits-728x90.png', href: 'https://bigego.games/games/privateerprofits', alt: 'Privateer Profits — rule the trade winds', weight: 1 },
  { id: 'bigegogames', img: '/ads/bigegogames-728x90.png', href: 'https://bigego.games', alt: 'Big Ego Games — games with an attitude', weight: 1 },
  { id: 'arcraz', img: '/ads/arcraz-728x90.png', href: 'https://arcraz.com', alt: 'Arcraz — Code. Craft. Deliver.', weight: 1 },
];

const AD_ROTATE_MS = 15_000; // rotate creatives every 15s

/**
 * Weighted pick that avoids repeating the current ad when possible, so the
 * rotation feels varied instead of landing on the same creative twice.
 */
export function pickAd(exclude?: string): HouseAd {
  const pool = HOUSE_ADS.filter((a) => a.id !== exclude);
  const from = pool.length > 0 ? pool : HOUSE_ADS;
  const total = from.reduce((s, a) => s + a.weight, 0);
  let r = Math.random() * total;
  for (const ad of from) {
    r -= ad.weight;
    if (r <= 0) return ad;
  }
  return from[from.length - 1]!;
}

export const HOUSE_PROVIDER_ID = 'house';

export class HouseAdProvider implements AdProvider {
  readonly id = HOUSE_PROVIDER_ID;

  isReady(format: AdFormat): boolean {
    // Banners are always bundled (no fill risk). Interstitial + rewarded are
    // ready whenever there's at least one bundled video creative.
    if (format === 'banner') return true;
    return HOUSE_VIDEO_ADS.length > 0;
  }

  mountBanner(host: HTMLElement, ctx: AdContext): BannerHandle {
    const slot = document.createElement('a');
    slot.className = 'play-chrome__ad';
    slot.target = '_blank';
    slot.rel = 'noopener noreferrer';
    slot.setAttribute('aria-label', 'Sponsor message');

    const img = document.createElement('img');
    img.width = 728;
    img.height = 90;
    img.decoding = 'async';
    slot.appendChild(img);
    host.appendChild(slot);

    let rotateAcc = 0;
    let current: HouseAd | null = null;

    const show = (ad: HouseAd): void => {
      current = ad;
      img.src = ad.img;
      img.alt = ad.alt;
      slot.href = ad.href;
      slot.dataset.adId = ad.id;
      slot.classList.remove('is-fading');
      void slot.offsetWidth;
      slot.classList.add('is-visible');
      recordAdEvent({
        slot: ctx.slot,
        adId: ad.id,
        event: 'impression',
        provider: this.id,
        format: 'banner',
        cartridgeId: ctx.cartridgeId ?? undefined,
        deviceId: ctx.deviceId,
      });
    };

    const onClick = (): void => {
      if (current) {
        recordAdEvent({
          slot: ctx.slot,
          adId: current.id,
          event: 'click',
          provider: this.id,
          format: 'banner',
          cartridgeId: ctx.cartridgeId ?? undefined,
          deviceId: ctx.deviceId,
        });
      }
    };
    slot.addEventListener('click', onClick);

    // Show immediately — the house banner is the whole reason the slot exists.
    show(pickAd());

    return {
      tick(deltaMs: number): void {
        rotateAcc += deltaMs;
        if (rotateAcc >= AD_ROTATE_MS) {
          rotateAcc = 0;
          slot.classList.add('is-fading');
          // swap at the midpoint of the fade
          window.setTimeout(() => show(pickAd(current?.id)), 220);
        }
      },
      dispose(): void {
        slot.removeEventListener('click', onClick);
        slot.remove();
      },
    };
  }

  // House video inventory (HOUSE_VIDEO_ADS) — plays a bundled clip full-screen.
  async showInterstitial(ctx: AdContext): Promise<InterstitialResult> {
    const outcome = await playHouseVideo('interstitial', ctx);
    // 'upgrade' → the viewer tapped the Remove-ads upsell; surface it so the
    // manager/caller opens the paywall. Otherwise 'completed'/'dismissed' both
    // mean the ad ran ('shown'); only a hard failure is 'unavailable'.
    if (outcome === 'upgrade') return { status: 'upgrade' };
    return { status: outcome === 'unavailable' ? 'unavailable' : 'shown' };
  }

  async showRewarded(ctx: AdContext): Promise<RewardedResult> {
    const outcome = await playHouseVideo('rewarded', ctx);
    // Reward is earned only by watching to the end. 'dismissed' → no token.
    if (outcome === 'completed') return { status: 'completed', adUnit: 'house-video' };
    if (outcome === 'dismissed') return { status: 'dismissed' };
    return { status: 'unavailable' };
  }
}
